import { TFunction } from "i18next";
import { DateTime } from "luxon";

import {
  AnalyticsActiveUsers,
  AnalyticsAudioPerPeriod,
  AnalyticsRequests,
  TokenUsagePerDay,
} from "@:openapi";

export enum RangeType {
  Today = "Today",
  Yesterday = "Yesterday",
  ThisWeek = "This week",
  ThisMonth = "This month",
  ThisYear = "This year",
  LastSevenDays = "Past week",
  LastThirtyDays = "Past month",
  LastSixMonths = "Past 6 months",
  LastYear = "Past year",
  Custom = "Custom",
}

/**
 * Stable translation-key slug per RangeType.
 * The enum VALUES are logic (they key getRangeValues() and drive comparisons),
 * so display labels are rendered with t(`analytics.range.${RANGE_TYPE_SLUG[rangeType]}`) instead of the raw value.
 */
export const RANGE_TYPE_SLUG: Record<RangeType, string> = {
  [RangeType.Today]: "today",
  [RangeType.Yesterday]: "yesterday",
  [RangeType.ThisWeek]: "thisWeek",
  [RangeType.ThisMonth]: "thisMonth",
  [RangeType.ThisYear]: "thisYear",
  [RangeType.LastSevenDays]: "pastWeek",
  [RangeType.LastThirtyDays]: "pastMonth",
  [RangeType.LastSixMonths]: "pastSixMonths",
  [RangeType.LastYear]: "pastYear",
  [RangeType.Custom]: "custom",
};

export const getRangeValues = () => {
  const today = DateTime.local();
  const yesterday = today.minus({ days: 1 });
  const tomorrow = today.plus({ days: 1 });

  return {
    [RangeType.Today]: [today.toISODate(), today.toISODate()],
    [RangeType.Yesterday]: [yesterday.toISODate(), yesterday.toISODate()],
    [RangeType.ThisWeek]: [
      today.startOf("week").toISODate(),
      today.toISODate(),
    ],
    [RangeType.ThisMonth]: [
      today.startOf("month").toISODate(),
      today.toISODate(),
    ],
    [RangeType.ThisYear]: [
      today.startOf("year").toISODate(),
      today.toISODate(),
    ],
    [RangeType.LastSevenDays]: [
      tomorrow.minus({ days: 7 }).toISODate(),
      today.toISODate(),
    ],
    [RangeType.LastThirtyDays]: [
      tomorrow.minus({ months: 1 }).toISODate(),
      today.toISODate(),
    ],
    [RangeType.LastSixMonths]: [
      tomorrow.minus({ months: 6 }).toISODate(),
      today.toISODate(),
    ],
    [RangeType.LastYear]: [
      tomorrow.minus({ years: 1 }).toISODate(),
      today.toISODate(),
    ],
    [RangeType.Custom]: [undefined, undefined],
  };
};

/**
 * The viewer's IANA timezone for timezone-aware charts
 */
export const getClientTimezone = (): string => {
  return DateTime.local().zoneName ?? "UTC";
};

/**
 * Full number below 10'000, otherwise 3 significant digits with a magnitude suffix
 */
export const formatCompact = (value: number): string => {
  if (value < 10000) {
    return value.toLocaleString();
  }

  const COMPACT_SUFFIXES = ["", "k", "M", "B", "T"];
  let tier = Math.min(
    Math.floor(Math.log10(value) / 3),
    COMPACT_SUFFIXES.length - 1,
  );

  let scaled = value / 1000 ** tier;

  // Rounding can bump 999'999 up to "1000k"; roll it into the next tier
  if (scaled >= 999.5 && tier < COMPACT_SUFFIXES.length - 1) {
    tier += 1;
    scaled = value / 1000 ** tier;
  }

  let decimals: number;
  if (scaled >= 100) {
    decimals = 0;
  } else if (scaled >= 10) {
    decimals = 1;
  } else {
    decimals = 2;
  }

  const numberPart = scaled.toFixed(decimals);
  const suffixPart = COMPACT_SUFFIXES[tier];

  return `${numberPart}${suffixPart}`;
};

/**
 * Human-readable duration from a number of seconds: "45s", "3m 20s", "2h 15m"
 */
export const formatDuration = (seconds: number): string => {
  const total = Math.round(seconds);
  if (total < 60) return `${total}s`;

  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;

  return `${minutes}m ${total % 60}s`;
};

/**
 * Compact single-unit duration for tight spots like chart axis ticks: "45s", "8m", "2.5h"
 */
export const formatDurationCompact = (seconds: number): string => {
  const total = Math.round(seconds);
  if (total < 60) return `${total}s`;
  if (total < 3600) return `${Math.round(total / 60)}m`;
  return `${Math.round(total / 360) / 10}h`;
};

export const symlog = (value: number) =>
  Math.sign(value) * Math.log10(1 + Math.abs(value));

export const symlogInvert = (value: number) =>
  Math.sign(value) * (10 ** Math.abs(value) - 1);

type FromDate = string;
type ToDate = string;

enum TimeUnit {
  Day = "day",
  Week = "week",
  Month = "month",
  Quarter = "quarter",
  Year = "year",
}

const formatTimeUnit = (timeUnit: TimeUnit) => {
  if (timeUnit === TimeUnit.Day) return "LLL d";
  if (timeUnit === TimeUnit.Week) return "'W'WW";
  if (timeUnit === TimeUnit.Month) return "LLL yyyy";
  if (timeUnit === TimeUnit.Quarter) return "yyyy - 'Q'q";
  if (timeUnit === TimeUnit.Year) return "yyyy";
  return "yyyy-MM-dd";
};

const getRangesTimeUnit = (timeUnit: TimeUnit, date: string) => {
  if (timeUnit === TimeUnit.Day) return { from: date, to: date };
  return {
    from: DateTime.fromISO(date).startOf(timeUnit).toISODate(),
    to: DateTime.fromISO(date).endOf(timeUnit).toISODate(),
  };
};

export const whatTimeUnit = (fromDate: FromDate, toDate: ToDate) => {
  const from = DateTime.fromISO(fromDate);
  const to = DateTime.fromISO(toDate);

  if (to.diff(from, "years").years >= 7) return TimeUnit.Year;
  if (to.diff(from, "quarters").quarters >= 3) return TimeUnit.Quarter;
  if (to.diff(from, "months").months >= 3) return TimeUnit.Month;
  if (to.diff(from, "weeks").weeks >= 3) return TimeUnit.Week;
  return TimeUnit.Day;
};

const generateRangeDates = (dates: string[], timeUnit: TimeUnit) => {
  const ranges: {
    shortLabel: string;
    timeUnit: TimeUnit;
    start: string;
    end: string;
  }[] = [];

  const trackingLabel = new Set<string>();

  for (const element of dates) {
    const { from, to } = getRangesTimeUnit(timeUnit, element);
    const label = DateTime.fromISO(element).toFormat(formatTimeUnit(timeUnit));
    if (trackingLabel.has(label)) continue;
    ranges.push({
      timeUnit,
      start: from as string,
      end: to as string,
      shortLabel: label,
    });
    trackingLabel.add(label);
  }

  return ranges;
};

const getPeriodTime = (fromDate: FromDate, toDate: ToDate) => {
  const from = DateTime.fromISO(fromDate);
  const to = DateTime.fromISO(toDate);
  const timeUnit = whatTimeUnit(fromDate, toDate);

  const mappingUnit = {
    [TimeUnit.Day]: "days",
    [TimeUnit.Week]: "weeks",
    [TimeUnit.Month]: "months",
    [TimeUnit.Quarter]: "quarters",
    [TimeUnit.Year]: "years",
  };

  const dates: string[] = [];
  let currentDate = from;
  while (currentDate <= to) {
    dates.push(currentDate.toISODate() as string);
    currentDate = currentDate.plus({ [mappingUnit[timeUnit]]: 1 });

    /*
     * Another time unit is range of time not a single date, so we have to add toDate to make sure
     * the last date is included in the range
     */
    if (currentDate > to && timeUnit !== TimeUnit.Day) {
      dates.push(to.toISODate() as string);
    }
  }

  return generateRangeDates(dates, timeUnit);
};

const isSeriesZero = (series: number[]) => {
  return series.every((item) => item === 0);
};

export const generateActiveUsersChart = (
  t: TFunction,
  from: FromDate,
  to: ToDate,
  activeUsersPeriodData?: AnalyticsActiveUsers[],
  seriesColor?: string,
) => {
  const periodTime = getPeriodTime(from, to);

  const xAxisData = periodTime.map((label) => {
    return label.shortLabel;
  });

  const activeUsers = periodTime.map(({ start, end, timeUnit }) => {
    const activeUser = activeUsersPeriodData
      ?.filter((item) => {
        if (timeUnit === TimeUnit.Day) return item.date === start;
        // Use inclusive range [start, end] to capture all data points within the period
        // e.g., for a week period ending on 2025-11-26, include data from that date
        return item.date >= start && item.date <= end;
      })
      .map((item) => item.numLogins);
    return activeUser?.reduce((acc, item) => acc + item, 0) ?? 0;
  });

  return {
    xAxis: [
      {
        id: "date",
        data: xAxisData,
        scaleType: "point" as const,
      },
    ],
    yAxis: [
      {
        min: 0,
        /*
         * If both series are zero, we need to set max to specific value to make sure x-axis and y-axis start at
         * the same point
         */
        max: isSeriesZero(activeUsers) ? 3 : undefined,
      },
    ],
    series: [
      {
        id: "activeUsers",
        label: t("analytics.activeUsers"),
        data: activeUsers,
        color: seriesColor,
      },
    ],
  };
};

export const generateRequestsChart = (
  t: TFunction,
  from: FromDate,
  to: ToDate,
  requestsPeriodData?: AnalyticsRequests[],
  seriesColor?: string,
) => {
  const periodTime = getPeriodTime(from, to);

  const xAxisData = periodTime.map((label) => {
    return label.shortLabel;
  });

  const requests = periodTime.map(({ start, end, timeUnit }) => {
    const request = requestsPeriodData
      ?.filter((item) => {
        if (timeUnit === TimeUnit.Day) return item.date === start;
        // Use inclusive range [start, end] to capture all data points within the period
        // e.g., for a week period ending on 2025-11-26, include data from that date
        return item.date >= start && item.date <= end;
      })
      .map((item) => item.numRequests);
    return request?.reduce((acc, item) => acc + item, 0) ?? 0;
  });

  return {
    xAxis: [
      {
        id: "date",
        data: xAxisData,
        scaleType: "point" as const,
      },
    ],
    yAxis: [
      {
        min: 0,
        /*
         * If both series are zero, we need to set max to specific value to make sure x-axis and y-axis start at
         * the same point
         */
        max: isSeriesZero(requests) ? 3 : undefined,
      },
    ],
    series: [
      {
        id: "numsOfRequests",
        label: t("analytics.numberOfRequests"),
        data: requests,
        color: seriesColor,
      },
    ],
  };
};

/**
 * Line chart for an audio duration series (seconds per period).
 * The backend returns per-day rows; here they are re-bucketed to the range's time unit and summed.
 */
export const generateDurationChart = (
  from: FromDate,
  to: ToDate,
  durationPeriodData?: AnalyticsAudioPerPeriod[],
  seriesColor?: string,
  seriesId = "seconds",
  seriesLabel = "Duration",
) => {
  const periodTime = getPeriodTime(from, to);

  const xAxisData = periodTime.map((label) => label.shortLabel);

  const seconds = periodTime.map(({ start, end, timeUnit }) => {
    const values = durationPeriodData
      ?.filter((item) => {
        if (timeUnit === TimeUnit.Day) return item.date === start;
        return item.date >= start && item.date <= end;
      })
      .map((item) => item.seconds);
    return values?.reduce((acc, item) => acc + item, 0) ?? 0;
  });

  return {
    xAxis: [
      {
        id: "date",
        data: xAxisData,
        scaleType: "point" as const,
      },
    ],
    yAxis: [
      {
        min: 0,
        max: isSeriesZero(seconds) ? 3 : undefined,
      },
    ],
    series: [
      {
        id: seriesId,
        label: seriesLabel,
        data: seconds,
        color: seriesColor,
      },
    ],
  };
};

export const generateTokenUsageChart = (
  t: TFunction,
  from: FromDate,
  to: ToDate,
  tokenUsagePerDayData?: TokenUsagePerDay[],
  inputSeriesColor?: string,
  outputSeriesColor?: string,
  dualAxis = false,
) => {
  const periodTime = getPeriodTime(from, to);

  const xAxisData = periodTime.map((label) => {
    return label.shortLabel;
  });

  const sumTokens = (key: "inputTokens" | "outputTokens") =>
    periodTime.map(({ start, end, timeUnit }) => {
      const tokens = tokenUsagePerDayData
        ?.filter((item) => {
          if (timeUnit === TimeUnit.Day) return item.date === start;
          // Use inclusive range [start, end] to capture all data points within the period
          return item.date >= start && item.date <= end;
        })
        .map((item) => item[key]);
      return tokens?.reduce((acc, item) => acc + item, 0) ?? 0;
    });

  const inputTokens = sumTokens("inputTokens");
  const outputTokens = sumTokens("outputTokens");

  const xAxis = [
    {
      id: "date",
      data: xAxisData,
      scaleType: "point" as const,
    },
  ];

  if (dualAxis) {
    return {
      xAxis,
      yAxis: [
        {
          id: "inputTokens",
          position: "left" as const,
          label: t("analytics.inputTokens"),
          min: 0,
          max: isSeriesZero(inputTokens) ? 3 : undefined,
        },
        {
          id: "outputTokens",
          position: "right" as const,
          label: t("analytics.outputTokens"),
          min: 0,
          max: isSeriesZero(outputTokens) ? 3 : undefined,
        },
      ],
      series: [
        {
          id: "inputTokens",
          yAxisId: "inputTokens",
          label: t("analytics.inputTokens"),
          data: inputTokens,
          color: inputSeriesColor,
        },
        {
          id: "outputTokens",
          yAxisId: "outputTokens",
          label: t("analytics.outputTokens"),
          data: outputTokens,
          color: outputSeriesColor,
        },
      ],
    };
  }

  const bothZero = isSeriesZero(inputTokens) && isSeriesZero(outputTokens);

  return {
    xAxis,
    yAxis: [{ min: 0, max: bothZero ? 3 : undefined }],
    series: [
      {
        id: "inputTokens",
        label: t("analytics.inputTokens"),
        data: inputTokens,
        color: inputSeriesColor,
      },
      {
        id: "outputTokens",
        label: t("analytics.outputTokens"),
        data: outputTokens,
        color: outputSeriesColor,
      },
    ],
  };
};

export const getTimezoneAwareDatetime = (
  date: FromDate | ToDate,
  boundary: "start" | "end" = "start",
): string => {
  // Example: 2025-07-24, start -> 2025‑07‑24T00:00:00.000+03:00
  //          2025-07-24, end   -> 2025‑07‑24T23:59:59.999+03:00

  const day = DateTime.fromISO(date, { zone: "local" });
  const bounded = boundary === "end" ? day.endOf("day") : day.startOf("day");

  return bounded.toISO() as string;
};
